import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Zap,
  Droplet,
  Lightbulb,
  Shield,
  Key,
  Settings,
  TrendingDown,
  Activity,
  Smartphone,
  Bell,
  Wrench,
  Leaf,
  ArrowRight,
  CheckCircle2,
  Send,
  MapPin
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { approaches } from '../data/mock';

const iconMap = {
  Zap,
  Droplet,
  Lightbulb,
  Shield,
  Key,
  Settings,
  TrendingDown,
  Activity,
  Smartphone,
  Bell,
  Wrench,
  Leaf
};

export const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    contact: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      toast.success('Mensagem enviada. Entraremos em contacto brevemente.');
      setFormData({
        name: '',
        organization: '',
        contact: '',
        message: ''
      });
      setIsSubmitting(false);
    }, 1500);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab"
            alt="Modern Institutional Building"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/80 to-slate-900/60" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Gestão Eficiente de Infraestruturas
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed">
              Sistemas integrados de monitorização e controlo para entidades públicas e privadas.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button size="lg" className="bg-brand-green hover:bg-brand-green-dark text-white px-8 py-6 text-lg font-semibold transition-all duration-300 hover:scale-105 hover:shadow-xl">
                  Pedir Contacto
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-white/50 rounded-full" />
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {approaches.map((approach) => {
              const Icon = iconMap[approach.icon];
              return (
                <div key={approach.id} className="text-center">
                  <div className="w-20 h-20 bg-brand-green/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Icon className="text-brand-green" size={36} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    {approach.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {approach.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Positioning Section */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <p className="text-xl text-gray-700 leading-relaxed">
              Soluções de monitorização, controlo e otimização para uma gestão mais eficiente de infraestruturas e ativos técnicos.
            </p>
            <p className="text-xl text-gray-700 leading-relaxed">
              Uma abordagem técnica, modular e adaptável à realidade de cada projeto.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Pedir Contacto
            </h2>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed">
              Preencha o formulário para uma primeira conversa.
            </p>
            <a href="#contacto">
              <Button size="lg" className="bg-brand-green hover:bg-brand-green-dark text-white px-10 py-6 text-lg font-semibold transition-all duration-300 hover:scale-105 hover:shadow-2xl">
                Pedir Contacto
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contacto" className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-xl">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name">Nome *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Nome completo"
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="organization">Entidade *</Label>
                  <Input
                    id="organization"
                    name="organization"
                    value={formData.organization}
                    onChange={handleChange}
                    required
                    placeholder="Nome da entidade"
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="contact">Email ou Telefone *</Label>
                  <Input
                    id="contact"
                    name="contact"
                    value={formData.contact}
                    onChange={handleChange}
                    required
                    placeholder="Contacto preferencial"
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="message">Mensagem</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={6}
                    placeholder="Descreva brevemente o seu pedido (opcional)"
                    className="mt-2"
                  />
                </div>

                <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <CheckCircle2 className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
                  <p className="text-sm text-blue-900">
                    Os seus dados serão tratados com confidencialidade de acordo com a nossa política de privacidade.
                  </p>
                </div>

                <Button
                  type="submit"
                  size="lg"
                  disabled={isSubmitting}
                  className="w-full bg-brand-green hover:bg-brand-green-dark text-white font-semibold transition-all duration-300 hover:scale-105"
                >
                  {isSubmitting ? (
                    'A enviar...'
                  ) : (
                    <>
                      Pedir Contacto
                      <Send className="ml-2" size={18} />
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
